import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, Observable, Subscription } from 'rxjs';
import { EDeviceModel } from 'src/app/conf/device-models';
import { ConnectedAutomations, Device } from 'src/app/model/device';
import { Setting } from 'src/app/model/setting';
import { SettingsCategory } from 'src/app/model/settings-category';
import { SettingPayload, SettingsPayload } from 'src/app/model/settings-payload';
import * as DeviceActions from 'src/app/store/device/device.actions';

import { AppState } from 'src/app/store/app.reducer';
import { RBE2423_CUSTOM_SETTINGS } from 'src/app/conf/settings';
import { selectAutomationAlias, selectAutomationsText, selectCurrentDevice, selectCurrentDeviceFeatures, selectCurrentDeviceInfo, selectCurrentDeviceSettingsCategoriesPaging, selectCurrentDeviceSettingsCategoriesPagingError, selectCurrentDeviceSettingsCategoriesPagingLoading, selectCurrentDeviceStatus, selectGroupsAll, selectPasswordValid, selectSettingsSaved } from 'src/app/store/device/device.selector';
import { DeviceStatus } from 'src/app/model/device-status';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { selectIsImpersMode, selectRoleExplainedTechnician } from 'src/app/store/user/user.selector';
import { PasswordDialogComponent } from '../password-dialog/password-dialog.component';
import { DeviceFeature } from 'src/app/model/device-feature';
import { SettingsCategoryPaging } from 'src/app/model/api/data-rest';
import { AutomationsAliasDialogComponent } from '../automations-alias-dialog/automations-alias-dialog.component';
import { AutomationsAlias, AutomationsText } from 'src/app/model/automations-alias';
import { ControlItem } from 'src/app/model/control-item';
import { CAGroup } from 'src/app/model/control-access';
import { MINFWVER_ACSCHEDULES_RBE24 } from 'src/app/conf/control-access';
import { DeviceInfo } from 'src/app/model/device-info';
import { ConfirmationService } from 'primeng/api';
import { RbeGenericEntryInfoDialogComponent } from '../rbe-generic-entry-info-dialog/rbe-generic-entry-info.dialog.component';

@Component({
	selector: 'app-settings',
	templateUrl: './settings.component.html',
	providers: [ConfirmationService, DialogService]
})

export class SettingsComponent implements OnInit, OnDestroy {
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	settingsPaging$: Observable<SettingsCategoryPaging | undefined>;
	settingsPagingSub: Subscription | undefined;
	passwordVerify$: Observable<boolean | undefined>;
	passwordVerifySub: Subscription | undefined;
	isTechnician$: Observable<boolean | undefined>;
	isTechnicianSub: Subscription | undefined;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;
	deviceFeature$: Observable<DeviceFeature[] | undefined>;
	deviceFeatureSub: Subscription | undefined;
	settingSaved$: Observable<boolean | undefined>;
	settingSavedSub: Subscription | undefined;
	settingsPagingLoading$: Observable<boolean | undefined>;
	settingsPagingLoadingSub: Subscription | undefined;
	settingsPagingError$: Observable<boolean | undefined>;
	settingsPagingErrorSub: Subscription | undefined;
	automationsAlias$: Observable<AutomationsAlias | undefined>;
	automationsAliasSub: Subscription | undefined;
	groups$: Observable<CAGroup[] | undefined>;
	groupsSub: Subscription | undefined;
	automationsText$: Observable<AutomationsText | undefined>;
	automationsTextSub: Subscription | undefined;
	deviceInfo$: Observable<DeviceInfo | undefined>;
	deviceInfoSub: Subscription | undefined;

	deviceInfo: DeviceInfo;
	loading: boolean;
	online: boolean;
	configured: number;
	connectedAutomation: ConnectedAutomations[];

	automationsText: AutomationsText;

	settingsForm: FormGroup;

	saving = false;
	opened = false;
	isTechnician: boolean;
	isImpersMode: boolean;
	passwordSupport: boolean;
	passwordVerify: boolean;

	settingsAll: SettingsCategory[] = [];
	settingsCategories: SettingsCategory[];
	customSettingsCategories: SettingsCategory[];
	offsets = [];
	displayScales = [];

	device: Device = new Device();
	automationsAlias: AutomationsAlias;

	groups: CAGroup[];

	showDiv = false;
	ref: DynamicDialogRef | undefined;
	TYPE_ALIAS_INPUT = "TYPE_ALIAS_INPUT";
	TYPE_ALIAS_OUTPUT = "TYPE_ALIAS_OUTPUT";
	TYPE_ALIAS_SELECTOR = "TYPE_ALIAS_SELECTOR";

	// Design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	MAX_MD_WIDTH = 1120;
	MIN_WIDTH_MX7 = 1700;
	acceptButtonStyle: string;
	rejectButtonStyle: string;

	constructor(
		private store: Store<AppState>,
		private fb: FormBuilder,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.device$ = this.store.select(selectCurrentDevice);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.settingsPaging$ = this.store.select(selectCurrentDeviceSettingsCategoriesPaging);
		this.passwordVerify$ = this.store.select(selectPasswordValid);
		this.isTechnician$ = this.store.select(selectRoleExplainedTechnician);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
		this.deviceFeature$ = this.store.select(selectCurrentDeviceFeatures);
		this.settingSaved$ = this.store.select(selectSettingsSaved);
		this.settingsPagingLoading$ = this.store.select(selectCurrentDeviceSettingsCategoriesPagingLoading);
		this.settingsPagingError$ = this.store.select(selectCurrentDeviceSettingsCategoriesPagingError);
		this.automationsAlias$ = this.store.select(selectAutomationAlias);
		this.groups$ = this.store.select(selectGroupsAll);
		this.automationsText$ = this.store.select(selectAutomationsText);
		this.deviceInfo$ = this.store.select(selectCurrentDeviceInfo);
	}

	ngOnInit(): void {
		this.settingsAll = [];
		this.passwordSupport = false;
		this.deviceSub = this.device$.subscribe(currentDevice => {
			this.device = currentDevice;
			if (currentDevice !== undefined) {
				this.connectedAutomation = currentDevice.ConnectedAutomations;
			} else {
				this.connectedAutomation = [];
			}
			if (this.isRbe() && this.isFunctionControlAccessSuppor()) {
				this.store.dispatch(DeviceActions.getGroupsAll());
			}
		});
		this.deviceStatusSub = this.deviceStatus$.pipe().subscribe(deviceState => {
			this.online = deviceState.Online;
			this.configured = deviceState.Configured;
		});
		this.settingsPagingLoadingSub = this.settingsPagingLoading$.pipe().subscribe(loading => {
			this.loading = loading;
		});
		this.settingsPagingErrorSub = this.settingsPagingError$.pipe().subscribe(error => {
			if (error) {
				this.settingsAll = [];
				this.settingsCategories = [];
				this.customSettingsCategories = [];
			}
		});
		this.settingsPagingSub = this.settingsPaging$.subscribe(settingsPaging => {
			if (settingsPaging !== undefined && settingsPaging.Registers !== undefined && settingsPaging.Registers.SettingsCategories !== undefined &&
				settingsPaging.Registers.SettingsCategories.length > 0) {
				this.mergeSettings(settingsPaging.Registers.SettingsCategories);
				//this.settingsAll = [...this.settingsAll, ...settingsPaging.Registers.SettingsCategories];
			}
			if (settingsPaging !== undefined && settingsPaging.Paging !== undefined && settingsPaging.Paging.PageNumber < settingsPaging.Paging.TotalPages) {
				this.store.dispatch(DeviceActions.getDeviceSettingsPaging({ page: settingsPaging.Paging.PageNumber + 1 }));
			} else if (settingsPaging !== undefined && settingsPaging.Paging !== undefined && settingsPaging.Paging.PageNumber === settingsPaging.Paging.TotalPages) {
				// All Settings
				this.offsets = [];
				this.displayScales = [];
				if (this.settingsAll.length > 0) {
					this.settingsCategories = this.buildForm(this.settingsAll);
					this.settingsCategories = this.settingsCategories.filter(category => category.Settings.length > 0);
					if (this.isRbe() && this.settingsCategories !== undefined) {
						this.createCustomSetting();
					}
				}
				this.saving = false;
				this.store.dispatch(DeviceActions.getDeviceSettingsPagingReset());
				// All SettingscurrentDeviceSettingsCategoriesPaging 
			}

		});
		this.store.dispatch(DeviceActions.getCommands({}));
		this.deviceFeatureSub = this.deviceFeature$.subscribe(deviceFeatures => {
			const fea = deviceFeatures.find(fe => fe.Name === 'password');
			if (fea !== undefined) {
				this.passwordSupport = true;
			}
		});
		this.passwordVerifySub = this.passwordVerify$.subscribe(passwordVerify => {
			this.passwordVerify = passwordVerify;
			if (this.passwordVerify) {
				this.loadSettings();
			}
		});
		this.isTechnicianSub = this.isTechnician$.subscribe((isTechnician => {
			this.isTechnician = isTechnician;
		}));
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
		this.settingSavedSub = this.settingSaved$?.subscribe(
			(settingSaved) => {
				if (settingSaved) {
					this.saving = false;
					this.loadSettings();
				}
			}
		);
		this.automationsAliasSub = this.automationsAlias$.subscribe(automationsAlias => {
			this.automationsAlias = automationsAlias;
		});
		this.groupsSub = this.groups$.subscribe(groups => {
			this.groups = groups;
			if (this.isRbe() && this.settingsCategories !== undefined) {
				this.createCustomSetting();
			}
		});
		this.automationsTextSub = this.automationsText$.subscribe(automationsText => {
			this.automationsText = automationsText;
		});
		this.deviceInfoSub = this.deviceInfo$.pipe(filter(d => !!d))
			.subscribe(currentDeviceInfo => {
				this.deviceInfo = currentDeviceInfo;
				if (this.isRbe() && this.isFunctionControlAccessSuppor()) {
					this.store.dispatch(DeviceActions.getGroupsAll());
				}
			});
		this.onResize();
	}

	isFunctionControlAccessSuppor(): boolean {
		return this.compareVersions(this.deviceInfo?.FwVersion, MINFWVER_ACSCHEDULES_RBE24);
	}

	compareVersions(ver1: string, ver2: string): boolean {
		// prevent missing versions v1 and/or v2
		if (!ver1) return false;
		if (!ver2) return true;

		const a = ver1.split('.').map(Number);
		const b = ver2.split('.').map(Number);

		const maxLength = Math.max(a.length, b.length);

		for (let i = 0; i < maxLength; i++) {
			const num1 = a[i] || 0; // if an element is missing, use 0 (default)
			const num2 = b[i] || 0;

			if (num1 > num2) return true;
			if (num1 < num2) return false;
		}

		return true; // if all parts are equal
	}

	ngOnDestroy(): void {
		this.deviceSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.settingsPagingSub?.unsubscribe();
		this.passwordVerifySub?.unsubscribe();
		this.isTechnicianSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
		this.deviceFeatureSub?.unsubscribe();
		this.settingsPagingLoadingSub?.unsubscribe();
		this.settingsPagingErrorSub?.unsubscribe();
		this.automationsAliasSub?.unsubscribe();
		this.groupsSub?.unsubscribe();
		this.automationsTextSub?.unsubscribe();
		this.deviceInfoSub?.unsubscribe();
	}

	togglePanel(opened: boolean): void {
		this.opened = opened;
		this.loadSettings();
	}

	/*getSettingsData(): void {
		if (this.opened && this.online) {
			this.store.dispatch(DeviceActions.getDeviceSettings({}));
		}
	}*/

	loadSettings(): void {
		if (this.opened && this.online && this.configured === 0) {
			this.loadSettingsRest();
		}
	}


	loadSettingsRest(): void {
		if (!this.passwordSupport || this.passwordVerify || (!this.isTechnician && !this.isImpersMode)) {
			this.settingsAll = [];
			this.settingsCategories = [];
			this.customSettingsCategories = [];
			this.store.dispatch(DeviceActions.getDeviceSettingsPagingReset());
			this.store.dispatch(DeviceActions.getDeviceSettingsPaging({ page: 1 }));
		} else {
			/* Open Dialog */
			this.ref = this.dialogService.open(PasswordDialogComponent, {
				header: this.translate.instant('PASSWORD'),
				width: '70vw',
				breakpoints: { '1600px': '98vw' },
				contentStyle: { overflow: 'auto' },
			});
		}
	}

	buildForm(categories: SettingsCategory[]): SettingsCategory[] {
		const formGroup = {};
		for (const category of categories) {
			for (let setting of category.Settings) {
				setting.Value = this.changeValueSettings(setting);
				setting = this.setStep(setting);
				setting = this.addOffset(setting);
				if (setting.Value !== null) {
					if (setting.ControlItem.Type === 'bitmask') {
						const itemsValue = [];
						let value = setting.Value;
						for (let i = 3; i > -1; i--) {
							if (value !== 0 && value / (2 ** i) >= 1) {
								itemsValue.unshift(true);
							} else {
								itemsValue.unshift(false);
							}
							value = value % (2 ** i);
						}
						const len = setting.ControlItem.Items.length;
						for (let i = 0; i < len; i++) {
							formGroup[setting.Id + '_b_' + setting.ControlItem.Items[i].Value] = [itemsValue[i]];
						}
					} else {
						if (setting.ControlItem.Type === 'combo' || setting.ControlItem.Type === 'color_picker') {
							const item = setting.ControlItem.Items.find(it => it.Value === setting.Value);
							formGroup[setting.Id] = [item];
						} else {
							formGroup[setting.Id] = [setting.Value];
						}
					}
				}
			}
		}
		this.settingsForm = this.fb.group(formGroup);
		return categories;
	}

	get f(): any {
		return this.settingsForm.controls;
	}

	isNull(settingCategory): boolean {
		let isNull = true;
		settingCategory.Settings.forEach(setting => {
			if (setting.Value !== null) {
				isNull = false;
			}
		});
		return isNull;
	}

	getDirtyValues(form: any): SettingPayload[] { // check if form values are changed
		const dirtyValues = [];
		const bitmaskMap = new Map<string, number>();

		Object.keys(form.controls)
			.forEach(key => {
				const currentControl = form.controls[key];

				if (currentControl.dirty) {
					if (currentControl.controls) { // recursive check for form arrays
						dirtyValues.push(this.getDirtyValues(currentControl));
					} else if (typeof key === 'string' && key.includes('_b_')) {
						const splitted = key.split('_b_');
						const value = currentControl.value ? 2 ** (Number(splitted[1])) - 1 : 0;
						if (!bitmaskMap.has(splitted[0])) {
							bitmaskMap.set(splitted[0], value);
						} else {
							bitmaskMap.set(splitted[0], bitmaskMap.get(splitted[0]) + value);
						}
					} else {
						let value;
						if (typeof currentControl.value === 'object') {
							value = currentControl.value.Value;
						} else {
							value = currentControl.value;
						}
						const offset = this.offsets.find(s => s.Id === +key);
						if (offset) {
							value -= offset.Offset;
						}
						const scale = this.displayScales.find(s => s.Id === +key);
						if (scale) {
							value = this.getValueFloat(value / scale.Scale);
						}
						dirtyValues.push({ Id: +key, Value: value });
					}
				}
			});

		if (bitmaskMap.size > 0) {
			for (const keyEdit of bitmaskMap.keys()) {
				const bitmaskValue = this.getBitmaskEditValue(form, keyEdit);
				dirtyValues.push({ Id: keyEdit, Value: bitmaskValue });
			}
		}
		return dirtyValues;
	}

	getBitmaskEditValue(form: any, keyEdit: string): number {
		let bitmaskValue = 0;
		Object.keys(form.controls)
			.forEach(key => {
				const currentControl = form.controls[key];
				if (currentControl.controls) { // recursive check for form arrays
					bitmaskValue = bitmaskValue + this.getBitmaskEditValue(form, keyEdit);
				} else if (typeof key === 'string' && key.includes(keyEdit + '_b_')) {
					const splitted = key.split('_b_');
					const value = currentControl.value ? Number(splitted[1]) : 0;
					bitmaskValue = bitmaskValue + value;
				}
			});
		return bitmaskValue;
	}

	submit(): void {
		this.saving = true;
		const changedValues = this.getDirtyValues(this.settingsForm);
		const settings: SettingsPayload = {
			Settings: changedValues,
		};
		this.store.dispatch(DeviceActions.saveDeviceSettings({ settings }));
	}

	getValueFloat(value: number): number {
		return parseFloat(value.toFixed(1));
	}

	getValueString(value: number, scale: number): string {
		if (Math.floor(scale) === 1) {
			return value.toFixed(0);
		}
		return value.toFixed(1);
	}

	scaledValueFloat(value: number, scale: number): number {
		return this.getValueFloat(value * scale);
	}

	scaledValueString(value: number, scale: number): string {
		return this.getValueString(value * scale, scale).replace(".", ",");
	}

	formattedSliderMin(setting: Setting): string {
		return this.scaledValueString(setting.ControlItem.Min, setting.ControlItem.DisplayScale);
	}

	formattedSliderMax(setting: Setting): string {
		return this.scaledValueString(setting.ControlItem.Max, setting.ControlItem.DisplayScale);
	}

	getSliderScaledMin(setting: Setting): number {
		return this.scaledValueFloat(setting.ControlItem.Min, setting.ControlItem.DisplayScale);
	}

	getSliderScaledMax(setting: Setting): number {
		return this.scaledValueFloat(setting.ControlItem.Max, setting.ControlItem.DisplayScale);
	}

	getSliderScaledStep(setting: Setting): number {
		return this.scaledValueFloat(setting.ControlItem.StepSize, setting.ControlItem.DisplayScale);
	}

	isSliderMin(setting: Setting): boolean {
		return this.f[setting.Id].value === this.getSliderScaledMin(setting);
	}

	isSliderMax(setting: Setting): boolean {
		return this.f[setting.Id].value === this.getSliderScaledMax(setting);
	}

	isRbe(): boolean {
		return this.device.ModelId !== undefined && (this.device.ModelId === EDeviceModel.RBE24230 || this.device.ModelId === EDeviceModel.RBE230 ||
			this.device.ModelId === EDeviceModel.RBE24 || this.device.ModelId === EDeviceModel.GEN_RBE24230);
	}

	isCustomCategoryRbe(category: SettingsCategory): boolean {
		return this.isRbe() && RBE2423_CUSTOM_SETTINGS.includes(category.Category);
	}


	onChangeInputSlider(setting: Setting) {
		this.f[setting.Id].setValue(this.f[setting.Id].value);
	}

	onChangeSlider(setting: Setting) {
		this.f[setting.Id].setValue(this.f[setting.Id].value);
	}

	createCustomSetting(): void {
		this.customSettingsCategories = [];
		for (const category of this.settingsCategories) {
			if (RBE2423_CUSTOM_SETTINGS.includes(category.Category)) {
				const mapCategory = new Map<string, Setting[]>();
				for (const setting of category.Settings) {
					let grouSetting = null;
					if (setting.Name.indexOf('srn1_output') > -1) {
						grouSetting = setting.Name.substring(11, 12);
					}
					if (setting.Name.indexOf('srn1_bus_selector_key_') > -1) {
						grouSetting = setting.Name.substring(22, 23);
					} else if (setting.Name.indexOf('srn1_bus_selector_') > -1) {
						grouSetting = setting.Name.substring(18, 19);
					}
					if (setting.Name.indexOf('srn1_input') > -1 && setting.Name.indexOf('srn1_input_control') < 0) { //srn1_input2polarization
						grouSetting = setting.Name.substring(10, 11);
					}
					if (setting.Name.indexOf('srn1_input_control_output_') > -1) {//srn1_input_control_output_1
						grouSetting = setting.Name.substring(26, 27);
					}
					if (setting.Name.indexOf('srn1_input_control_automation_') > -1) {//srn1_input_control_automation_1
						grouSetting = setting.Name.substring(30, 31);
					}
					if (setting.Name.indexOf('srn1_in') > -1 && (setting.Name.indexOf('_cmd') > -1 || setting.Name.indexOf('_groups') > -1)) {
						grouSetting = setting.Name.substring(7, 8);
						if (setting.Name.indexOf('_groups') > -1) {
							setting.ControlItem = this.changeControlItemGroupValue(setting.ControlItem);
						}
					}
					//srn1_in1_autom1_cmd
					//srn1_in1_autom2_cmd
					//srn1_in2_autom1_cmd
					//srn1_in3_autom1_cmd
					//srn1_in4_autom1_cmd
					//srn1_in1_groups
					if (grouSetting !== null && setting.Value !== null) {
						if (!mapCategory.has(grouSetting)) {
							mapCategory.set(grouSetting, [setting]);
						} else {
							mapCategory.get(grouSetting).push(setting);
						}
					}
				}
				category.CustomSettings = mapCategory;
				this.customSettingsCategories.push(category);
			}
		}
	}

	changeValueSettings(setting: Setting): number {
		/*if (setting.Name.indexOf('srn1_in') > -1 && (setting.Name.indexOf('_cmd') > -1 || setting.Name.indexOf('_groups') > -1) && setting.Value === null) {
			return 0;
		}*/
		return setting.Value;
	}

	changeControlItemGroupValue(controlItem: ControlItem): ControlItem {
		if (controlItem.Items !== undefined && controlItem.Items.length > 0) {
			for (let i = 0; i < controlItem.Items.length; i++) {
				const numGroup = controlItem.Items[i].Description.substring(controlItem.Items[i].Description.length - 1, controlItem.Items[i].Description.length);
				if (this.groups !== undefined && this.groups.length > 0) {
					const group = this.groups.find(gr => (gr.Position + 1).toString() === numGroup);
					if (group) {
						controlItem.Items[i].Description = controlItem.Items[i].Description + ' (' + group.Name + ')';
					}
				}
				controlItem.Items[i].Description = this.translateString(controlItem.Items[i].Description);
			}
		}
		return controlItem;
	}

	translateString(txt: string): string {
		let resStr = '';
		const fields = txt.split('{');
		for (const field of fields) {
			if (field.indexOf('}') >= 0) {
				const toTranslate = field.split('}');
				resStr = resStr + this.translateText(toTranslate[0]) + toTranslate[1];
			} else {
				resStr = resStr + field;
			}
		}
		return resStr;
	}

	translateText(txt: string): string {
		if (this.automationsText !== undefined && this.automationsText[txt]) {
			return this.automationsText[txt];
		}
		return txt;
	}

	setSliderValue(setting: Setting, value: number): boolean {
		let newValue = this.f[setting.Id].value / setting.ControlItem.DisplayScale + value * setting.ControlItem.StepSize;
		newValue = this.getValueFloat(newValue);
		if (newValue < setting.ControlItem.Min) {
			return false;
		}
		if (newValue > setting.ControlItem.Max) {
			return false;
		}
		// const oldValue = this.f[setting.Id].value;
		newValue = this.scaledValueFloat(newValue, setting.ControlItem.DisplayScale);
		this.f[setting.Id].setValue(newValue);
		this.f[setting.Id].markAsDirty();
		return true;
	}

	setStep(setting: Setting): Setting {
		const sett = { ...setting };
		if (sett.ControlItem.Type === 'slider') {
			if (!sett.ControlItem.StepSize) {
				sett.ControlItem.StepSize = 1;
			}
			if (!sett.ControlItem.DisplayScale) {
				sett.ControlItem.DisplayScale = 1;
			}
			if (sett.ControlItem.DisplayScale !== 1) {
				sett.Value = this.scaledValueFloat(sett.Value, sett.ControlItem.DisplayScale);
			}
			this.displayScales.push({ Id: sett.Id, Scale: sett.ControlItem.DisplayScale });
		}
		return sett;
	}

	addOffset(setting: Setting): Setting {
		const sett = { ...setting };
		if (sett.Offset) {
			sett.ControlItem.Min += sett.Offset;
			sett.ControlItem.Max += sett.Offset;
			sett.Value += sett.Offset;
			this.offsets.push({ Id: sett.Id, Offset: sett.Offset });
		}
		return sett;
	}

	editAliasOutput(): void {
		this.store.dispatch(DeviceActions.resetEditAutomationsAlias());
		this.ref = this.dialogService.open(AutomationsAliasDialogComponent, {
			header: this.translate.instant('OUTPUT_ALIAS'),
			width: '70vw',
			breakpoints: { '1250px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				typeAlias: this.TYPE_ALIAS_OUTPUT,

			}
		});
	}

	editAliasInput(): void {
		this.store.dispatch(DeviceActions.resetEditAutomationsAlias());
		this.ref = this.dialogService.open(AutomationsAliasDialogComponent, {
			header: this.translate.instant('INPUT_ALIAS'),
			width: '70vw',
			breakpoints: { '1250px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				typeAlias: this.TYPE_ALIAS_INPUT
			}
		});
	}

	editAliasSelector(): void {
		this.store.dispatch(DeviceActions.resetEditAutomationsAlias());
		this.ref = this.dialogService.open(AutomationsAliasDialogComponent, {
			header: this.translate.instant('SELECTOR_ALIAS'),
			width: '70vw',
			breakpoints: { '1250px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				typeAlias: this.TYPE_ALIAS_SELECTOR
			}
		});
	}

	getOutputName(id: string): string {
		if (this.automationsAlias !== undefined && this.automationsAlias.Outputs !== undefined) {
			for (const output of this.automationsAlias.Outputs) {
				if (output.Id.toString() === id && output.Description !== output.DefaultDescription) {
					return ' (' + this.translateData(output.Description) + ')';
				}
			}
		}
		return '';
		//return ' (' + this.outputName(id) + ')';
	}

	getInputName(id: string): string {
		if (this.automationsAlias !== undefined && this.automationsAlias.Inputs !== undefined) {
			for (const input of this.automationsAlias.Inputs) {
				if (input.Id.toString() === id && input.Description !== input.DefaultDescription) {
					return ' (' + this.translateData(input.Description) + ')';
				}
			}
		}
		return '';
	}

	getSelectorName(id: string): string {
		if (this.automationsAlias !== undefined && this.automationsAlias.Selectors !== undefined) {
			for (const selector of this.automationsAlias.Selectors) {
				if (selector.Id.toString() === id && selector.Description !== selector.DefaultDescription) {
					return ' (' + this.translateData(selector.Description) + ')';
				}
			}
		}
		return '';
	}

	outputName(id: string): string {
		if (this.automationsAlias !== undefined && this.automationsAlias.Outputs !== undefined) {
			for (const output of this.automationsAlias.Outputs) {
				if (output.Id.toString() === id && output.Description !== output.DefaultDescription) {
					return this.translateData(output.Description);
				}
			}
		}
		return '';
	}

	getOutputNameSelector(setting: Setting, output): string {
		// output
		if (setting.Name.indexOf('srn1_bus_selector') > -1 && setting.Name.indexOf('output') > -1) {
			return this.outputName(output.Description);
		}
		if (setting.Name.indexOf('srn1_input_control_') > -1 && setting.Name.indexOf('output') > -1) {
			return this.outputName(output.Description);
		}

		// Automation
		if ((setting.Name.indexOf('srn1_input_control_') > -1 || setting.Name.indexOf('srn1_bus_selector') > -1) &&
			setting.Name.indexOf('automation') > -1) {
			if (output.Value === 1 && this.connectedAutomation !== undefined && this.connectedAutomation.length > 0) {
				const automation = this.connectedAutomation.find(au => au.CrpItemId === 1);
				return automation !== undefined ? automation.Name : '';
			}
			if (output.Value === 2 && this.connectedAutomation !== undefined && this.connectedAutomation.length > 0) {
				const automation = this.connectedAutomation.find(au => au.CrpItemId === 2);
				return automation !== undefined ? automation.Name : '';
			}
			if (output.Value === 4 && this.connectedAutomation !== undefined && this.connectedAutomation.length > 0) {
				const automation = this.connectedAutomation.find(au => au.CrpItemId === 3);
				return automation !== undefined ? automation.Name : '';
			}
			if (output.Value === 8 && this.connectedAutomation !== undefined && this.connectedAutomation.length > 0) {
				const automation = this.connectedAutomation.find(au => au.CrpItemId === 4);
				return automation !== undefined ? automation.Name : '';
			}
		}
		return '';
	}

	getColorSettingValue(settValue): string {
		if (settValue !== undefined) {
			const color = settValue.Color
			switch (color) {
				case 'white':
					return '#ffffff';
				case 'yellow':
					return '#ffc107';
				case 'orange':
					return '#ff6900';
				case 'red':
					return '#f44336';
				case 'violet':
					return '#9c27b0';
				case 'blue':
					return '#03a9f4';
				case 'lightblue':
					return '#00bcd4';
				case 'green':
					return '#4caf50';
				default:
					return '';
			}
		}
		return '';
	}

	isAccess(): boolean {
		if (!this.passwordSupport) {
			return true;
		}
		if (this.passwordVerify) {
			return true;
		}
		if (!this.isTechnician && !this.isImpersMode) {
			return true;
		}
		return false;
	}

	mergeSettings(settings: SettingsCategory[]): void {
		for (let i = 0; i < this.settingsAll.length; i++) {
			const settMerge = settings.filter(st => st.Category === this.settingsAll[i].Category);
			if (settMerge !== undefined && settMerge.length > 0) {
				this.settingsAll[i].Settings = [...this.settingsAll[i].Settings, ...settMerge[0].Settings]
			}
		}
		for (const sett of settings) {
			const settFind = this.settingsAll.filter(st => st.Category === sett.Category);
			if (settFind === undefined || settFind.length === 0) {
				this.settingsAll.push(sett);
			}
		}
	}


	translateData(str: string): string {
		let resStr = '';
		const fields = str.split('{');
		for (const field of fields) {
			if (field.indexOf('}') >= 0) {
				const toTranslate = field.split('}');
				resStr = resStr + this.translate.instant(toTranslate[0]) + toTranslate[1];
			} else {
				resStr = resStr + field;
			}
		}
		return resStr;
	}

	viewBusSelectorsCustomSettingsRbe(settingCategory: any): boolean {
		return !(settingCategory.Category === 'bus_selectors' && settingCategory.CustomSettings.size === 0)
	}

	autoSavingSrn1OperatingMode(setting: any): void {
		if ((setting.Id === 199 || setting.Name === 'srn1_events_log') && this.settingsForm.controls[setting.Id].value.Value === 1) {
			this.infoDialogGenericEntry(setting.Description)
		}
		const cate = this.settingsAll.find(set => set.Category === 'settings');
		let settingOld = undefined;
		if (cate !== undefined) {
			settingOld = cate.Settings.find(set => set.Name === 'srn1_operating_mode');
		}
		if (cate !== undefined && settingOld !== undefined) {
			if (setting.Id === 225 || setting.Name === 'srn1_operating_mode') {
				this.acceptButtonStyle = "p-button-danger";
				this.rejectButtonStyle = "p-button-secondary";
				this.confirmationService.confirm({
					header: this.translate.instant('AUTOSAVE_SETTING_RESET'),
					message: this.translate.instant('AUTOSAVE_SETTING_RESET_WARNING'),
					acceptLabel: this.translate.instant('CONFIRM'),
					rejectLabel: this.translate.instant('CANCEL'),
					icon: 'pi cm-alert',
					accept: () => {
						this.loading = true;
						this.submit();
					},
					reject: () => {
						const item = setting.ControlItem.Items.find(it => it.Value === settingOld.Value);
						this.settingsForm.controls[setting.Id].reset(item);
					}
				});
			}
		}
	}

	infoDialogGenericEntry(description: string) {
		this.ref = this.dialogService.open(RbeGenericEntryInfoDialogComponent, {
			header: this.translate.instant('MTMA_CONF_{Information}') + ': ' + description,
			contentStyle: { overflow: 'auto' },
			data: {}
		})
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return (this.screenWidth <= 660);
	}

	underTablet(): boolean {
		return this.screenWidth <= this.MAX_MD_WIDTH;
	}

	dynamicBorderLabelMobile(classType: string): string {
		switch (classType) {
			case 'borderRow':
				if (this.underTablet()) {
					return "row";
				}
				return "col-12 p-0 justify-content-between border-bottom-1 border-100";
			case 'borderInput':
				if (this.underTablet()) {
					return "col-12 w-full p-0";
				}
				return "pl-2";
			case 'label':
				if (this.underTablet()) {
					return "col-12 p-1 justify-content-center";
				}
				return "pr-2";
			default:
				return "";
		}
	}

	dynamicInputTextWidth(): string {
		return (this.underTablet()) ? "w-full h-2_5rem" : "w-30rem h-2rem";
	}

	showErrors(type: SettingsComponent["f"], classType: string): string {
		let inputTextStyle = "";

		switch (classType) {
			case 'borderRow':
				if (this.onExtendedMobile()) {
					return "row";
				}
				return ((type.errors !== null) && (type.dirty)) ? "border-bottom-1 border-50" : "border-bottom-1 border-100";
			case 'borderInput':
				if (this.onExtendedMobile()) {
					inputTextStyle += "col-12 w-full p-0";
				}
				if ((type.errors !== null) && (type.dirty)) {
					inputTextStyle += " border-bottom-1 border-50";
				} else {
					inputTextStyle += " border-bottom-1 border-100";
				}
				return inputTextStyle;
			case 'label':
				if (this.onExtendedMobile()) {
					inputTextStyle += "col-12 p-1";
				}
				if ((type.errors !== null) && (type.dirty)) {
					inputTextStyle += " text-50";
				}
				return inputTextStyle;
			default:
				return inputTextStyle;
		}
	}
}
